import { createFileRoute, useRouter } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { AppShell } from "@/components/AppShell";
import type { NotificationKind } from "@/lib/notifications";
import {
  listMyNotifications,
  markAllNotificationsRead,
  markNotificationRead,
} from "@/lib/notifications.functions";
import { Bell, Package, Truck, MessageSquare, AlertTriangle, CheckCircle2, DollarSign, Sparkles } from "lucide-react";
import { useState } from "react";

export const Route = createFileRoute("/_authenticated/notifications")({
  head: () => ({ meta: [{ title: "Notifications — Want To Buy" }] }),
  component: NotificationsPage,
});

const ICONS: Record<NotificationKind, typeof Bell> = {
  new_offer: Package,
  offer_accepted: CheckCircle2,
  shipped: Truck,
  delivered: CheckCircle2,
  dispute: AlertTriangle,
  release: DollarSign,
  message: MessageSquare,
  system: Sparkles,
};

const TONES: Record<NotificationKind, string> = {
  new_offer: "text-primary bg-primary/10",
  offer_accepted: "text-success bg-success/10",
  shipped: "text-blue-300 bg-blue-500/10",
  delivered: "text-success bg-success/10",
  dispute: "text-destructive bg-destructive/10",
  release: "text-success bg-success/10",
  message: "text-primary bg-primary/10",
  system: "text-muted-foreground bg-muted",
};

function asKind(type: string | null | undefined): NotificationKind {
  return (type && (type in ICONS)) ? (type as NotificationKind) : "system";
}

function timeAgo(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 7) return `${d}d ago`;
  return new Date(iso).toLocaleDateString();
}

function NotificationsPage() {
  const [filter, setFilter] = useState<"all" | "unread">("all");
  const fetchList = useServerFn(listMyNotifications);
  const markAll = useServerFn(markAllNotificationsRead);
  const markOne = useServerFn(markNotificationRead);
  const qc = useQueryClient();
  const router = useRouter();

  const { data, isLoading } = useQuery({
    queryKey: ["notifications", "list"],
    queryFn: () => fetchList(),
  });

  const markAllMut = useMutation({
    mutationFn: () => markAll(),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["notifications"] }),
  });
  const markOneMut = useMutation({
    mutationFn: (id: string) => markOne({ data: { id } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["notifications"] }),
  });

  const items = data?.notifications ?? [];
  const unread = items.filter((n) => !n.read_at).length;
  const visible = filter === "unread" ? items.filter((n) => !n.read_at) : items;

  function openNotif(n: { id: string; read_at: string | null; link: string | null }) {
    if (!n.read_at) markOneMut.mutate(n.id);
    if (!n.link) return;
    if (/^https?:\/\//i.test(n.link)) {
      window.open(n.link, "_blank", "noopener,noreferrer");
    } else {
      router.history.push(n.link);
    }
  }

  return (
    <AppShell>
      <Header unread={unread} filter={filter} setFilter={setFilter} onMarkAll={() => markAllMut.mutate()} />
      <div className="panel mt-6 divide-y divide-border">
        {isLoading && <div className="p-10 text-center text-sm text-muted-foreground">Loading…</div>}
        {!isLoading && visible.length === 0 && (
          <div className="p-10 text-center text-sm text-muted-foreground">
            {items.length === 0 ? "No notifications yet — activity on your orders and offers will appear here." : "You're all caught up."}
          </div>
        )}
        {visible.map((n) => {
          const kind = asKind(n.type);
          const Icon = ICONS[kind];
          const isRead = !!n.read_at;
          return (
            <button
              key={n.id}
              type="button"
              onClick={() => openNotif(n)}
              className={`block w-full text-left ${n.link ? "cursor-pointer" : "cursor-default"}`}
            >
              <div className={`flex gap-3 p-4 transition hover:bg-muted/40 ${!isRead ? "bg-primary/[0.03]" : ""}`}>
                <div className={`grid h-10 w-10 shrink-0 place-items-center rounded-md ${TONES[kind]}`}>
                  <Icon className="h-5 w-5" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-start justify-between gap-3">
                    <div className="font-semibold text-sm">{n.title}</div>
                    <div className="shrink-0 text-[10px] uppercase tracking-wider text-muted-foreground">{timeAgo(n.created_at)}</div>
                  </div>
                  {n.body && <div className="mt-0.5 text-sm text-muted-foreground line-clamp-2">{n.body}</div>}
                </div>
                {!isRead && <div className="mt-2 h-2 w-2 shrink-0 rounded-full bg-primary" />}
              </div>
            </button>
          );
        })}
      </div>
    </AppShell>
  );
}

function Header({
  unread,
  filter,
  setFilter,
  onMarkAll,
}: {
  unread: number;
  filter: "all" | "unread";
  setFilter: (f: "all" | "unread") => void;
  onMarkAll: () => void;
}) {
  return (
    <div className="flex flex-wrap items-end justify-between gap-3">
      <div>
        <h1 className="font-display text-3xl font-bold">Notifications</h1>
        <p className="text-sm text-muted-foreground">{unread} unread · activity across your orders & offers</p>
      </div>
      <div className="flex items-center gap-2">
        <div className="flex rounded-md border border-border bg-panel p-1 text-xs">
          {(["all", "unread"] as const).map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`rounded px-3 py-1.5 capitalize ${filter === f ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}
            >
              {f}
            </button>
          ))}
        </div>
        <button onClick={onMarkAll} className="rounded-md border border-border bg-panel px-3 py-2 text-xs hover:bg-muted">
          Mark all read
        </button>
      </div>
    </div>
  );
}
